import React, { useState, useEffect } from 'react';
import { Session } from '../api/types';
import Sheet from './ui/Sheet';
import { WarningIcon } from './ui/icons';
import { parseServerDate, formatClock } from '../utils/dateUtils';
import './AbandonSheet.css';

export interface AbandonData {
  currentCrawlName: string;
  newPubName: string;
}

interface AbandonSheetProps {
  isOpen: boolean;
  data: AbandonData | null;
  session: Session | null;
  onAbandon: () => void;
  onCancel: () => void;
}

/**
 * Says the old crawl will be *submitted*, not abandoned — it does go on the
 * leaderboard, and the previous wording ("abandon") implied it was binned.
 */
const AbandonSheet: React.FC<AbandonSheetProps> = ({
  isOpen,
  data,
  session,
  onAbandon,
  onCancel,
}) => {
  const [lastData, setLastData] = useState<AbandonData | null>(data);
  const [lastSession, setLastSession] = useState<Session | null>(session);

  useEffect(() => {
    if (data) setLastData(data);
  }, [data]);

  useEffect(() => {
    if (session) setLastSession(session);
  }, [session]);

  const displayData = data ?? lastData;
  const displaySession = session ?? lastSession;

  if (!displayData) {
    return <Sheet isOpen={isOpen} onClose={onCancel} testId="abandon-sheet" />;
  }

  const completedPubs = displaySession?.pubs.filter(pub => pub.isComplete).length ?? 0;
  const totalPubs = displaySession?.pubs.length ?? 0;
  const elapsed = displaySession
    ? formatClock(
        Math.max(
          0,
          Math.floor(
            (Date.now() - parseServerDate(displaySession.sessionStartTime).getTime()) / 1000
          )
        )
      )
    : null;

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onCancel}
      title="That pub's on another crawl"
      testId="abandon-sheet"
    >
      <p className="abandon-sheet-note">
        <b>{displayData.newPubName}</b> isn't on your current crawl:{' '}
        <b>{displayData.currentCrawlName}</b>.
      </p>

      {displaySession && (
        <div className="abandon-sheet-summary">
          <span className="abandon-sheet-summary-label">
            Your {displayData.currentCrawlName}
            <b>
              {completedPubs} of {totalPubs} pubs
            </b>
          </span>
          <span className="abandon-sheet-summary-time tabular">{elapsed}</span>
        </div>
      )}

      <div className="abandon-sheet-warning">
        <WarningIcon />
        <p>
          Starting the new crawl will <b>submit your {displayData.currentCrawlName} as it stands</b>.
          It goes on the board, but you can't add to it afterwards.
        </p>
      </div>

      <button className="abandon-sheet-primary" onClick={onAbandon}>
        Submit and start the new crawl
      </button>
      <button className="abandon-sheet-secondary" onClick={onCancel}>
        Stay on {displayData.currentCrawlName}
      </button>
    </Sheet>
  );
};

export default AbandonSheet;
